<template>
  <div class="points-recharge">
    <h2>积分充值</h2>
    
    <!-- 用户积分信息 -->
    <div class="user-points-info">
      <el-card class="points-card">
        <div class="points-header">
          <!-- 积分余额显示 -->
          <div class="points-balance">
            <h3>当前积分余额</h3>
            <p class="balance">{{ userPoints }} <span class="unit">积分</span></p>
            <p class="equivalent">约 ¥{{ (userPoints / 100).toFixed(2) }}</p>
          </div>
          <!-- 使用进度环形图 -->
          <div class="points-trend">
            <el-progress
              type="circle"
              :percentage="pointsPercentage"
              :width="100"
              :stroke-width="10"
              color="#409EFF"
            >
              <template #default>
                <span class="progress-text">{{ pointsPercentage }}%</span>
              </template>
            </el-progress>
            <p class="trend-text">本月使用进度</p>
          </div>
        </div>
        
        <!-- 积分详情统计 -->
        <div class="points-details">
          <el-row :gutter="20">
            <el-col :span="8">
              <div class="detail-item">
                <span class="label">本月已用</span>
                <span class="value used">{{ monthlyUsed }} 积分</span>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="detail-item">
                <span class="label">本月剩余</span>
                <span class="value remaining">{{ monthlyRemaining }} 积分</span>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="detail-item">
                <span class="label">充值总额</span>
                <span class="value total">{{ totalRecharged }} 积分</span>
              </div>
            </el-col>
          </el-row>
        </div>
      </el-card>
    </div>

    <!-- 充值套餐 -->
    <div class="recharge-packages">
      <h3>选择充值套餐</h3>
      <div class="packages-grid">
        <div
          v-for="pkg in packages"
          :key="pkg.id"
          class="package-card"
          :class="{ 'recommended': pkg.recommended, 'selected': selectedPackage === pkg.id }"
          @click="selectPackage(pkg.id)"
        >
          <div class="package-header" v-if="pkg.recommended">
            <el-tag type="warning" size="small">推荐</el-tag>
          </div>
          <div class="package-content">
            <h4 class="package-name">{{ pkg.name }}</h4>
            <p class="package-points">{{ pkg.points }} 积分</p>
            <p class="package-price">¥{{ pkg.price }}</p>
            <p class="package-discount" v-if="pkg.discount">
              节省 ¥{{ pkg.discount }}
            </p>
            <p class="package-desc">{{ pkg.description }}</p>
          </div>
          <div class="package-footer">
            <el-button
              type="primary"
              :class="{ 'selected-btn': selectedPackage === pkg.id }"
              @click.stop="selectPackage(pkg.id)"
            >
              {{ selectedPackage === pkg.id ? '已选择' : '立即购买' }}
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 充值金额 -->
    <div class="custom-amount">
      <h3>自定义充值金额</h3>
      <div class="amount-input">
        <el-input
          v-model="customAmount"
          placeholder="输入充值金额（元）"
          type="number"
          :min="1"
          :max="10000"
          @input="calculateCustomPoints"
        >
          <template #prepend>¥</template>
          <template #append>
            <span class="points-equivalent">≈ {{ customPoints }} 积分</span>
          </template>
        </el-input>
        <div class="quick-amounts">
          <el-button
            v-for="amount in quickAmounts"
            :key="amount"
            :type="customAmount === amount.toString() ? 'primary' : ''"
            @click="setQuickAmount(amount)"
          >
            ¥{{ amount }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 支付方式 -->
    <div class="payment-methods">
      <h3>选择支付方式</h3>
      <div class="methods-grid">
        <div
          v-for="method in paymentMethods"
          :key="method.id"
          class="method-card"
          :class="{ 'selected': selectedMethod === method.id }"
          @click="selectMethod(method.id)"
        >
          <div class="method-icon">
            <el-icon :size="30">
              <component :is="method.icon" />
            </el-icon>
          </div>
          <div class="method-info">
            <h4>{{ method.name }}</h4>
            <p>{{ method.description }}</p>
          </div>
          <div class="method-check">
            <el-icon v-if="selectedMethod === method.id" color="#409EFF">
              <CircleCheck />
            </el-icon>
          </div>
        </div>
      </div>
    </div>

    <!-- 订单信息 -->
    <div class="order-summary">
      <el-card>
        <h3>订单信息</h3>
        <div class="order-details">
          <div class="order-row">
            <span class="label">充值套餐：</span>
            <span class="value">{{ selectedPackageName }}</span>
          </div>
          <div class="order-row" v-if="customAmount > 0">
            <span class="label">自定义金额：</span>
            <span class="value">¥{{ customAmount }} ({{ customPoints }} 积分)</span>
          </div>
          <div class="order-row">
            <span class="label">支付方式：</span>
            <span class="value">{{ selectedMethodName }}</span>
          </div>
          <div class="order-row total">
            <span class="label">总计：</span>
            <span class="value">¥{{ totalAmount }} ({{ totalPoints }} 积分)</span>
          </div>
        </div>
        
        <div class="order-actions">
          <el-button type="primary" size="large" @click="handleRecharge" :loading="loading">
            立即充值
          </el-button>
          <el-button size="large" @click="resetOrder">
            重置
          </el-button>
        </div>
      </el-card>
    </div>

    <!-- 充值记录 -->
    <div class="recharge-history">
      <h3>充值记录</h3>
      <el-table :data="rechargeHistory" style="width: 100%" stripe>
        <el-table-column prop="orderId" label="订单号" />
        <el-table-column prop="date" label="充值时间" />
        <el-table-column prop="amount" label="充值金额">
          <template #default="{ row }">
            ¥{{ row.amount }}
          </template>
        </el-table-column>
        <el-table-column prop="points" label="获得积分" />
        <el-table-column prop="method" label="支付方式"  />
        <el-table-column prop="status" label="状态" >
          <template #default="{ row }">
            <el-tag :type="getStatusTagType(row.status)" size="small">
              {{ row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100">
          <template #default="{ row }">
            <el-button type="text" @click="viewOrderDetail(row)">
              详情
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import {
  CircleCheck,
  CreditCard,
  Coin
} from '@element-plus/icons-vue'

export default {
  name: 'PointsRechargeView',
  components: {
    CircleCheck,
    CreditCard,
    Coin
  },
  data() {
    return {
      userPoints: 12500,
      monthlyUsed: 3200,
      monthlyLimit: 10000,
      totalRecharged: 50000,
      selectedPackage: 2,
      customAmount: '',
      customPoints: 0,
      selectedMethod: 'alipay',
      loading: false,
      packages: [
        {
          id: 1,
          name: '基础套餐',
          points: 1000,
          price: '10.00',
          description: '适合轻度用户',
          recommended: false
        },
        {
          id: 2,
          name: '标准套餐',
          points: 5000,
          price: '45.00',
          discount: '5.00',
          description: '性价比最高',
          recommended: true
        },
        {
          id: 3,
          name: '高级套餐',
          points: 10000,
          price: '85.00',
          discount: '15.00',
          description: '适合重度用户',
          recommended: false
        },
        {
          id: 4,
          name: '企业套餐',
          points: 50000,
          price: '400.00',
          discount: '100.00',
          description: '适合团队使用',
          recommended: false
        }
      ],
      quickAmounts: [10, 50, 100, 200, 500],
      paymentMethods: [
        {
          id: 'alipay',
          name: '支付宝',
          icon: 'CreditCard',
          description: '推荐使用，即时到账'
        },
        {
          id: 'wechat',
          name: '微信支付',
          icon: 'ChatDotSquare',
          description: '扫码支付，安全快捷'
        }
      ],
      rechargeHistory: [
        
      ]
    }
  },
  computed: {
    pointsPercentage() {
      return Math.round((this.monthlyUsed / this.monthlyLimit) * 100)
    },
    monthlyRemaining() {
      return this.monthlyLimit - this.monthlyUsed
    },
    selectedPackageName() {
      const pkgItem = this.packages.find(p => p.id === this.selectedPackage)
      return pkgItem ? `${pkgItem.name} (${pkgItem.points}积分)` : '未选择'
    },
    selectedMethodName() {
      const method = this.paymentMethods.find(m => m.id === this.selectedMethod)
      return method ? method.name : '未选择'
    },
    selectedPackagePrice() {
      const pkgItem = this.packages.find(p => p.id === this.selectedPackage)
      return pkgItem ? parseFloat(pkgItem.price) : 0
    },
    totalAmount() {
      const packageAmount = this.selectedPackagePrice
      const customAmount = parseFloat(this.customAmount) || 0
      return (packageAmount + customAmount).toFixed(2)
    },
    totalPoints() {
      const pkgItem = this.packages.find(p => p.id === this.selectedPackage)
      const packagePoints = pkgItem ? pkgItem.points : 0
      const customPoints = this.customPoints
      return packagePoints + customPoints
    }
  },
  methods: {
    selectPackage(id) {
      this.selectedPackage = id
    },
    selectMethod(id) {
      this.selectedMethod = id
    },
    setQuickAmount(amount) {
      this.customAmount = amount.toString()
      this.calculateCustomPoints()
    },
    calculateCustomPoints() {
      const amount = parseFloat(this.customAmount) || 0
      // 1元 = 100积分
      this.customPoints = Math.round(amount * 100)
    },
    handleRecharge() {
      if (this.totalAmount <= 0) {
        this.$message.warning('请选择充值套餐或输入充值金额')
        return
      }

      this.loading = true
      // 模拟支付过程
      setTimeout(() => {
        this.loading = false
        this.$confirm(
          `确认支付 ¥${this.totalAmount} 购买 ${this.totalPoints} 积分吗？`,
          '确认支付',
          {
            confirmButtonText: '确认支付',
            cancelButtonText: '取消',
            type: 'warning'
          }
        ).then(() => {
          // 模拟支付成功
          const newOrder = {
            orderId: `ORD${new Date().getTime()}`,
            date: new Date().toLocaleString('zh-CN'),
            amount: parseFloat(this.totalAmount),
            points: this.totalPoints,
            method: this.selectedMethodName,
            status: '成功'
          }
          
          this.rechargeHistory.unshift(newOrder)
          this.userPoints += this.totalPoints
          this.totalRecharged += this.totalPoints
          
          this.$message.success('充值成功！积分已到账')
          this.resetOrder()
        }).catch(() => {
          this.$message.info('已取消支付')
        })
      }, 1000)
    },
    resetOrder() {
      this.selectedPackage = 2
      this.customAmount = ''
      this.customPoints = 0
      this.selectedMethod = 'alipay'
    },
    viewOrderDetail(row) {
      this.$message.info(`查看订单详情: ${row.orderId}`)
      // 实际项目中这里可以打开订单详情对话框
    },
    getStatusTagType(status) {
      const map = {
        '成功': 'success',
        '失败': 'danger',
        '处理中': 'warning',
        '已取消': 'info'
      }
      return map[status] || 'info'
    }
  },
  mounted() {
    this.calculateCustomPoints()
  }
}
</script>

<style scoped>
.points-recharge {
  padding: 20px;
}

.user-points-info {
  margin-bottom: 30px;
}

.points-card {
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.points-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.points-balance h3 {
  margin: 0 0 10px 0;
  color: #606266;
  font-size: 16px;
  font-weight: normal;
}

.balance {
  font-size: 36px;
  font-weight: bold;
  color: #409EFF;
  margin: 0 0 5px 0;
}

.balance .unit {
  font-size: 18px;
  color: #909399;
}

.equivalent {
  color: #909399;
  margin: 0;
  font-size: 14px;
}

.points-trend {
  text-align: center;
}

.progress-text {
  font-size: 18px;
  font-weight: bold;
  color: #409EFF;
}

.trend-text {
  margin-top: 10px;
  color: #909399;
  font-size: 14px;
}

.points-details {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.detail-item .label {
  color: #606266;
  font-size: 14px;
  margin-bottom: 5px;
}

.detail-item .value {
  font-size: 18px;
  font-weight: bold;
}

.detail-item .used {
  color: #F56C6C;
}

.detail-item .remaining {
  color: #67C23A;
}

.detail-item .total {
  color: #409EFF;
}

.recharge-packages {
  margin-bottom: 30px;
}

.recharge-packages h3 {
  margin-bottom: 20px;
  color: #303133;
}

.packages-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.package-card {
  background: #fff;
  border: 2px solid #e4e7ed;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
  overflow: hidden;
}

.package-card:hover {
  border-color: #409EFF;
  transform: translateY(-5px);
  box-shadow: 0 5px 20px rgba(64, 158, 255, 0.2);
}

.package-card.recommended {
  border-color: #E6A23C;
}

.package-card.recommended:hover {
  border-color: #E6A23C;
  box-shadow: 0 5px 20px rgba(230, 162, 60, 0.2);
}

.package-card.selected {
  border-color: #409EFF;
  background-color: #f0f7ff;
}

.package-header {
  position: absolute;
  top: 10px;
  right: 10px;
}

.package-content {
  text-align: center;
  margin: 20px 0;
}

.package-name {
  margin: 0 0 10px 0;
  color: #303133;
  font-size: 18px;
}

.package-points {
  font-size: 24px;
  font-weight: bold;
  color: #409EFF;
  margin: 0 0 10px 0;
}

.package-price {
  font-size: 20px;
  font-weight: bold;
  color: #F56C6C;
  margin: 0 0 5px 0;
}

.package-discount {
  color: #67C23A;
  font-size: 14px;
  margin: 0 0 10px 0;
}

.package-desc {
  color: #909399;
  font-size: 14px;
  margin: 0;
}

.package-footer {
  text-align: center;
}

.selected-btn {
  background-color: #67C23A;
  border-color: #67C23A;
}

.custom-amount {
  margin-bottom: 30px;
}

.custom-amount h3 {
  margin-bottom: 15px;
  color: #303133;
}

.amount-input {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.points-equivalent {
  color: #409EFF;
  font-weight: bold;
}

.quick-amounts {
  margin-top: 15px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.payment-methods {
  margin-bottom: 30px;
}

.payment-methods h3 {
  margin-bottom: 15px;
  color: #303133;
}

.methods-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
}

.method-card {
  background: #fff;
  border: 2px solid #e4e7ed;
  border-radius: 8px;
  padding: 15px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s;
}

.method-card:hover {
  border-color: #409EFF;
}

.method-card.selected {
  border-color: #409EFF;
  background-color: #f0f7ff;
}

.method-icon {
  margin-right: 15px;
  color: #409EFF;
}

.method-info {
  flex: 1;
}

.method-info h4 {
  margin: 0 0 5px 0;
  color: #303133;
}

.method-info p {
  margin: 0;
  color: #909399;
  font-size: 12px;
}

.method-check {
  width: 24px;
  height: 24px;
}

.order-summary {
  margin-bottom: 30px;
}

.order-summary h3 {
  margin-bottom: 15px;
  color: #303133;
}

.order-details {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.order-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.order-row:last-child {
  margin-bottom: 0;
}

.order-row .label {
  color: #606266;
}

.order-row .value {
  color: #303133;
  font-weight: bold;
}

.order-row.total {
  border-top: 1px solid #e4e7ed;
  padding-top: 10px;
  margin-top: 10px;
}

.order-row.total .value {
  color: #F56C6C;
  font-size: 18px;
}

.order-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.recharge-history h3 {
  margin-bottom: 15px;
  color: #303133;
}
</style>
